// The bottom scrubber (a SoundCloud-style timeline). The AI's inline
// contributions are placed as rounded colour/icon chips along a horizontal track
// at the position of the transcript line each one anchors to; a playhead marks
// the current scroll position. Dragging the track scrolls the vertical chat;
// clicking a chip jumps the feed to that line. Visual cue: the "Sentiment
// Timeline" reference — a thin track, tinted chips, a compact legend.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { KIND_STYLE, TONE, TONE_CHIP } from '@/components/shell/aiTurnStyle'
import { cn } from '@/lib/utils'
import type { AiTurn, AiTurnKind } from '@/lib/ai/types'

interface Marker {
  id: string
  kind: AiTurnKind
  segId: string | null
  fraction: number // 0..1 position along the track
  completed: boolean // ticked off → dimmed chip
  streaming: boolean // still pending → show the caption, not the body
  text: string // the contribution body (for the hover preview)
  detail?: string // the one-clause "why"
  caption?: string // pending label, shown while streaming
}

// Kinds shown in the legend, in escalation order.
const LEGEND: AiTurnKind[] = ['candidate', 'insight', 'caution', 'note', 'present']

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n
}

export function SignalTimeline({
  turns,
  scrollRef,
  onJump,
  onToggleDone
}: {
  turns: AiTurn[]
  scrollRef: React.RefObject<HTMLDivElement | null>
  onJump: (segmentId: string | null) => void
  onToggleDone?: (id: string) => void
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [markers, setMarkers] = useState<Marker[]>([])
  const [playhead, setPlayhead] = useState(0)
  const [tick, setTick] = useState(0)
  // Which chip's hover preview is open. A short close delay (a ref'd timer) lets
  // the pointer travel from chip to popover without it snapping shut.
  const [hovered, setHovered] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function openPreview(id: string) {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setHovered(id)
  }
  function closePreview() {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setHovered(null), 120)
  }

  // Re-measure when the feed scrolls or resizes (positions + playhead shift).
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const bump = () => setTick((t) => t + 1)
    el.addEventListener('scroll', bump, { passive: true })
    const ro = new ResizeObserver(bump)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', bump)
      ro.disconnect()
    }
  }, [scrollRef])

  // Measure each contribution's vertical position within the scroll content and
  // map it to a 0..1 fraction; not-yet-rendered anchors land at the live edge.
  useLayoutEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const total = root.scrollHeight || 1
    const rootTop = root.getBoundingClientRect().top
    const next = turns.map((t) => {
      let fraction = 1
      if (t.afterSegmentId === null) {
        fraction = 0
      } else {
        const sel = window.CSS?.escape ? window.CSS.escape(t.afterSegmentId) : t.afterSegmentId
        const node = root.querySelector<HTMLElement>(`[data-segid="${sel}"]`)
        if (node) {
          const top = node.getBoundingClientRect().top - rootTop + root.scrollTop
          fraction = clamp01(top / total)
        }
      }
      return {
        id: t.id,
        kind: t.kind,
        segId: t.afterSegmentId,
        fraction,
        completed: !!t.completed,
        streaming: t.streaming,
        text: t.text,
        detail: t.detail,
        caption: t.label
      }
    })
    setMarkers(next)
    const range = root.scrollHeight - root.clientHeight
    setPlayhead(range > 0 ? clamp01(root.scrollTop / range) : 0)
  }, [turns, tick, scrollRef])

  // Drag / click the track → scroll the feed to that fraction.
  const dragging = useRef(false)
  function scrubTo(clientX: number) {
    const track = trackRef.current
    const root = scrollRef.current
    if (!track || !root) return
    const rect = track.getBoundingClientRect()
    const fraction = clamp01((clientX - rect.left) / (rect.width || 1))
    const range = root.scrollHeight - root.clientHeight
    root.scrollTop = fraction * range
  }
  function onPointerDown(e: React.PointerEvent) {
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    scrubTo(e.clientX)
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragging.current) scrubTo(e.clientX)
  }
  function onPointerUp(e: React.PointerEvent) {
    dragging.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  return (
    <div className="select-none">
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="relative h-9 cursor-pointer rounded-md border border-border bg-sidebar"
      >
        {/* playhead */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 w-px bg-accent"
          style={{ left: `${playhead * 100}%` }}
        >
          <span className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rounded-full bg-accent" />
        </div>

        {/* signal chips */}
        {markers.map((m) => {
          const style = KIND_STYLE[m.kind]
          return (
            <button
              key={m.id}
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onPointerEnter={() => openPreview(m.id)}
              onPointerLeave={closePreview}
              onClick={(e) => {
                e.stopPropagation()
                onJump(m.segId)
              }}
              className={cn(
                'absolute top-1/2 grid size-5 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-md border transition-transform hover:scale-110',
                TONE_CHIP[style.tone],
                m.completed && 'opacity-40 saturate-50',
                hovered === m.id && 'scale-110 ring-1 ring-accent'
              )}
              style={{ left: `${m.fraction * 100}%` }}
            >
              <style.Icon className="size-3" />
            </button>
          )
        })}

        {/* hover preview — full content + a done toggle for the hovered chip */}
        {markers.map((m) =>
          hovered === m.id ? (
            <SignalPreview
              key={`pv_${m.id}`}
              marker={m}
              onEnter={() => openPreview(m.id)}
              onLeave={closePreview}
              onToggleDone={onToggleDone}
            />
          ) : null
        )}
      </div>

      {/* legend (popover lives inside the track, above) */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        {LEGEND.map((kind) => {
          const style = KIND_STYLE[kind]
          return (
            <span
              key={kind}
              className="flex items-center gap-1 text-label text-muted-foreground"
            >
              <span
                className={cn('grid size-3.5 place-items-center rounded-sm border', TONE_CHIP[style.tone])}
              >
                <style.Icon className="size-2.5" />
              </span>
              {style.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

// The hover card for one scrubber chip: full contribution text + context and a
// done toggle, so the room can scan and check off contributions without jumping
// the live feed. Positioned above its chip; hugs the track edges so it never
// spills off-screen at the start/end of the timeline.
function SignalPreview({
  marker,
  onEnter,
  onLeave,
  onToggleDone
}: {
  marker: Marker
  onEnter: () => void
  onLeave: () => void
  onToggleDone?: (id: string) => void
}) {
  const style = KIND_STYLE[marker.kind]
  const tone = TONE[style.tone]
  const align =
    marker.fraction < 0.2
      ? 'translate-x-0'
      : marker.fraction > 0.8
        ? '-translate-x-full'
        : '-translate-x-1/2'
  const body = marker.streaming ? marker.caption || 'Working…' : marker.text
  return (
    <div
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      onPointerDown={(e) => e.stopPropagation()}
      style={{ left: `${marker.fraction * 100}%` }}
      className={cn(
        'absolute bottom-full z-20 mb-2 w-72 max-w-[78vw] rounded-lg border bg-popover p-3 text-left shadow-lg',
        tone.border,
        align
      )}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className={cn(
            'flex items-center gap-1 text-label font-semibold tracking-wide uppercase',
            tone.text
          )}
        >
          <style.Icon className="size-3.5 shrink-0" />
          {style.label}
        </span>
        {onToggleDone && !marker.streaming && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggleDone(marker.id)
            }}
            className={cn(
              'ml-auto flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-label transition-colors',
              marker.completed
                ? 'border-success bg-success text-bg'
                : 'border-border text-muted-foreground hover:border-success/60 hover:text-success'
            )}
          >
            <Check className="size-3" strokeWidth={3} />
            {marker.completed ? 'Done' : 'Mark done'}
          </button>
        )}
      </div>
      <p
        className={cn(
          'max-h-40 overflow-y-auto text-body-sm whitespace-pre-wrap text-text',
          marker.streaming && 'text-muted-foreground italic'
        )}
      >
        {body}
      </p>
      {marker.detail && !marker.streaming && (
        <p className="mt-1.5 text-label text-muted-foreground">↳ {marker.detail}</p>
      )}
    </div>
  )
}
